/**
 * User interface for paging:
 * 
 * ```
 *   [1] ...  9   >
 * <  1  [2] ...  9  >
 * <  1  ... [3] ... 9 >
 * <  1  ... [7] ... 9 >
 * <  1  ... [8]  9  >
 * <  1  ... [9]
 * ```
 * 
 * @module
 */

import { assertNonNullable } from '@rauschma/helpers/typescript/type.js';
import { type VNode } from 'preact';
import { useContext } from 'preact/hooks';
import { PageInfo, PageInfoContext } from '../../core/page-info.js';

import './page-navigation.css';

export interface PageHandlingCallback {
  callback: (pageIndex: number) => void;
}
export interface PageHandlingWebPaths {
  absWebPaths: Array<string>;
}

export type NavigationPageHandling = PageHandlingCallback | PageHandlingWebPaths;

export function PageNavigation({pageCount, pageIndex, pageHandling}: {pageCount: number, pageIndex: number, pageHandling: NavigationPageHandling}) {
  const pageInfo = useContext(PageInfoContext);
  assertNonNullable(pageInfo);

  if (pageCount <= 1) {
    // Only one page, don’t display the navigation user interface
    return null;
  }
  const lastPageIndex = pageCount-1;
  const elements = new Array<VNode|string>();
  {
    // Previous page
    const linkText = '<';
    if (pageIndex > 0) {
      elements.push(createPageLink(pageInfo, pageHandling, pageIndex-1, linkText));
    } else {
      const style = {
        visibility: 'hidden',
      };
      elements.push(<span class="page-nav-span" style={style}>{linkText}</span>);
    }
  }

  // First page (meaningful because pageCount >= 2)
  const firstPageLinkText = '1';
  if (pageIndex === 0) {
    elements.push(<b class="page-nav-span">{firstPageLinkText}</b>);
  } else {
    elements.push(createPageLink(pageInfo, pageHandling, 0, firstPageLinkText));
  }

  if (pageIndex >= 2) {
    // There is at least one page between the first page and the current page.
    elements.push(<Ellipsis />);
  }

  if (pageIndex > 0 && pageIndex < lastPageIndex)
  elements.push(<b class="page-nav-span">{pageIndex+1}</b>);
  
  if (pageIndex <= lastPageIndex-2) {
    // There is at least one page between the current page and the last page.
    elements.push(<Ellipsis />);
  }

  // Last page (meaningful because pageCount >= 2)
  const lastPageLinkText = String(pageCount);
  if (pageIndex === lastPageIndex) {
    elements.push(<b class="page-nav-span">{lastPageLinkText}</b>);
  } else {
    elements.push(createPageLink(pageInfo, pageHandling, lastPageIndex, lastPageLinkText));
  }

  if (pageIndex < lastPageIndex) {
    // Next page
    const linkText = '>';
    if (pageIndex < lastPageIndex) {
      elements.push(createPageLink(pageInfo, pageHandling, pageIndex+1, linkText));
    } else {
      const style = {
        visibility: 'hidden',
      };
      elements.push(<span class="page-nav-span" style={style}>{linkText}</span>);
    }
  }

  return <nav class="page-navigation">
    {elements}
  </nav>;

  function Ellipsis() {
    return <a class="page-nav-span" href="#" onClick={handleClick}>...</a>;
    function handleClick(event: MouseEvent) {
      event.preventDefault();
      const pageNumberString = prompt('Go to page number:', String(pageIndex+1));
      if (pageNumberString) {
        const pageNumber = Number(pageNumberString);
        if (!Number.isNaN(pageNumber) && Number.isInteger(pageNumber) && pageNumber >= 1 && pageNumber <= pageCount) {
          const pageIndex = pageNumber-1;
          if ('callback' in pageHandling) {
            setTimeout(() => pageHandling.callback(pageIndex), 0)
          } else {
            assertNonNullable(pageInfo);
            const absWebPath = pageHandling.absWebPaths[pageIndex];
            const peerWebPath = pageInfo.createPeerWebPath(absWebPath);
            location.href = peerWebPath;
          }
        }
      }
    }
  }
}

function createPageLink(pageInfo: PageInfo, pageHandling: NavigationPageHandling, pageIndex: number, linkText: string): VNode {
  if ('callback' in pageHandling) {
    const {callback} = pageHandling;
    return <a class="page-nav-span" href="" onClick={handleClick.bind(null, pageIndex)}>{linkText}</a>
    function handleClick(pageIndex: number, event: MouseEvent) {
      event.preventDefault();
      callback(pageIndex);
    }
  } else {
    assertNonNullable(pageInfo);
    const absWebPath = pageHandling.absWebPaths[pageIndex];
    const peerWebPath = pageInfo.createPeerWebPath(absWebPath);
    return <a class="page-nav-span" href={peerWebPath}>{linkText}</a>;
  }
}